import React, { type ReactNode } from 'react';
import clsx from 'clsx';
import { AlertCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '../components/buttons/Button';

interface SmallSectionProps {
	title: string | ReactNode;
	subtitle?: string | ReactNode;
	children: React.ReactNode;
	className?: string;
	useExpanded?: boolean;
	expandedOnLoad?: boolean;
}

export function SmallSection({ title, subtitle, children, className, useExpanded = false, expandedOnLoad = true }: SmallSectionProps) {
	const [expanded, setExpanded] = React.useState(expandedOnLoad);
	return (
		<div className={clsx('rounded-lg border border-gray-200 bg-white', className && className)}>
			<div className="flex h-[40px] flex-row items-center gap-2 border-b border-gray-100 px-4 py-2.5">
				{useExpanded && (
					<Button
						appearance="ghost"
						Icon={!expanded ? ChevronRight : ChevronDown}
						aria-label="Expand"
						onClick={() => setExpanded(!expanded)}
						className="px-1 py-0.5"
					/>
				)}
				<h3 className="text-xs font-semibold uppercase tracking-wide text-gray-700">{title}</h3>
				<span className="text-[10px] text-gray-400">{subtitle}</span>
			</div>
			{expanded && children}
		</div>
	);
}

export function SmallSectionFieldRow({
	label,
	value,
	flag,
	flagNote,
	span = false
}: {
	label: string;
	value?: string | null;
	flag?: boolean;
	flagNote?: string;
	span?: boolean;
}) {
	return (
		<div className={span ? 'col-span-2' : ''}>
			<dt className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">{label}</dt>
			<dd className={`text-xs leading-relaxed ${flag ? 'font-medium text-maximumRed' : 'text-gray-700'}`}>
				{value || <span className="italic text-gray-300">Not recorded</span>}
			</dd>
			{flag && flagNote && (
				<p className="mt-0.5 flex items-start gap-1 text-[10px] text-maximumRed">
					<AlertCircle size={10} className="mt-0.5 shrink-0" />
					{flagNote}
				</p>
			)}
		</div>
	);
}
